
import React from "react";
import { Site } from "@/services/types";
import { MapPin, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SiteGoogleMapProps {
  site: Site;
  className?: string;
}

const SiteGoogleMap: React.FC<SiteGoogleMapProps> = ({ site, className = "" }) => {
  // Générer l'URL Google Maps pour les directions
  const getDirectionsUrl = (site: Site) => {
    const query = encodeURIComponent(`${site.address}, ${site.postalCode} ${site.city}`);
    return `https://www.google.com/maps/dir/?api=1&destination=${query}`;
  };

  // Générer l'URL Google Maps pour afficher le lieu
  const getPlaceUrl = (site: Site) => {
    if (site.coordinates && site.coordinates.lat && site.coordinates.lng) {
      return `https://www.google.com/maps/place/${site.coordinates.lat},${site.coordinates.lng}`;
    }
    const query = encodeURIComponent(`${site.name} ${site.address}, ${site.postalCode} ${site.city}`);
    return `https://www.google.com/maps/search/${query}`;
  };

  // Générer l'URL d'embed pour l'iframe
  const getEmbedUrl = (site: Site) => {
    const baseUrl = "https://www.google.com/maps/embed/v1/place";
    const apiKey = "AIzaSyBkRcnHgBuwc4v-pz69p2UXml2skVY93CU";
    
    let query = "";
    if (site.coordinates && site.coordinates.lat && site.coordinates.lng) {
      query = `${site.coordinates.lat},${site.coordinates.lng}`;
    } else {
      query = encodeURIComponent(`${site.name} ${site.address}, ${site.postalCode} ${site.city}`);
    }
    
    return `${baseUrl}?key=${apiKey}&q=${query}&zoom=15`;
  };

  return (
    <div className={`bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-fabre-green to-fabre-teal p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white/20 rounded-full p-2">
              <MapPin className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Localisation</h3>
              <p className="text-white/90 text-sm">Trouvez-nous facilement</p>
            </div>
          </div>
          <Button
            asChild
            variant="secondary"
            size="sm"
            className="bg-white/20 hover:bg-white/30 text-white border-white/30"
          >
            <a 
              href={getPlaceUrl(site)} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-2"
            >
              <ExternalLink className="h-4 w-4" />
              <span>Google Maps</span>
            </a>
          </Button>
        </div>
      </div>

      {/* Map */}
      <div className="relative">
        <iframe
          src={getEmbedUrl(site)}
          width="100%"
          height="400"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title={`Carte de ${site.name}`}
          className="w-full"
        />
        
        {/* Overlay avec informations */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg border">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-1">{site.name}</h4>
                <p className="text-sm text-gray-600 mb-2">
                  {site.address}<br />
                  {site.postalCode} {site.city}
                </p>
                {site.phone && (
                  <a 
                    href={`tel:${site.phone}`}
                    className="text-sm text-fabre-green hover:text-fabre-darkgreen font-medium"
                  >
                    {site.phone}
                  </a>
                )}
              </div>
              <Button
                asChild
                size="sm"
                className="bg-fabre-green hover:bg-fabre-darkgreen text-white ml-4"
              >
                <a 
                  href={getDirectionsUrl(site)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center space-x-1"
                >
                  <MapPin className="h-4 w-4" />
                  <span>Itinéraire</span>
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteGoogleMap;
